import dayjs from 'dayjs';
import * as taxApi from '../../lib/api/taxbill';
import * as yup from 'yup';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  listTaxbills,
  setTaxbillsFilters,
  resetTaxbillsFilters,
} from '../../modules/taxbillModule/taxbills';
import { Box, Button, Chip, Stack, Typography, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useFormik } from 'formik';

import { useBoolean } from '../../hooks/useBoolean';

import TaxbillList from '../../components/taxbillmng/TaxbillList';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import { TaxbillListBox } from '../../components/taxbillmng/TaxbillListBox';
import TaxbillStatusList from '../../components/taxbillmng/TaxbillStatusList';
import AskDialog from '../../components/common/dialog/AskDialog';
import TaxbillTotalSummary from '../../components/taxbillmng/TaxbillTotalSummary';
import TaxbillEditModal from '../../components/taxbillmng/TaxbillEditModal';
import IssueApprovalRequestDialog from '../../components/taxbillmng/IssueApprovalRequestDialog';

const TaxbillListContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const { totalPrice, taxbills, defaultFilters, error, loading } = useSelector(
    ({ taxbillModule, loading }) => ({
      totalPrice: taxbillModule.taxbills.totalPrice,
      taxbills: taxbillModule.taxbills.taxbills,
      defaultFilters: taxbillModule.taxbills.defaultFilters,
      error: taxbillModule.taxbills.error,
      loading: loading['taxbill/LIST_TAXBILLS'],
    }),
  );

  const [tableData, setTableData] = useState([]);

  // 세금계산서 발행 요청 결재(스위치), 2026-10-21 - 스위치 상태에 따라 발행요청이
  // 바로 처리되거나(OFF) 결재선 선택 다이얼로그를 먼저 띄운다(ON).
  const [approvalEnabled, setApprovalEnabled] = useState(false);
  useEffect(() => {
    taxApi.getIssueFlowConfig().then((res) => {
      setApprovalEnabled(Boolean(res?.data?.approvalEnabled));
    });
  }, []);

  const makeFilters = (filters) => {
    const startDate =
      filters.searchStrtDt !== null && filters.searchStrtDt !== ''
        ? dayjs(filters.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      filters.searchEndDt !== null && filters.searchEndDt !== ''
        ? dayjs(filters.searchEndDt).format('YYYY-MM-DD')
        : '';

    const searchData = {
      osTran: filters.searchOsTran,
      invTyp: filters.searchInvTyp,
      searchTyp: filters.searchTyp,
      reqCompYn: filters.searchYn,
      [filters.searchKeyword]: filters.searchWords,
      strtDt: startDate,
      endDt: endDate,
    };

    return searchData;
  };

  useEffect(() => {
    dispatch(listTaxbills(makeFilters(defaultFilters)));
  }, [dispatch, defaultFilters]);

  useEffect(() => {
    if (taxbills) {
      setTableData(taxbills);
    }
  }, [taxbills]);

  const handleFilters = useCallback(
    (name, value) => {
      const newFilters = { ...defaultFilters, [name]: value };

      dispatch(setTaxbillsFilters(newFilters));
    },
    [defaultFilters, dispatch],
  );

  const [itemType, setItemType] = useState(null);

  const [selectedCancle, setSelectedCancle] = useState(null);
  const showCancle = useBoolean();
  const handleCancle = useCallback((row) => {
    showCancle.onTrue();
    setSelectedCancle(row);
  }, []);

  const onCancleCancle = () => {
    showCancle.onFalse();
    setSelectedCancle(null);
  };
  const onCancleConfirm = () => {
    //발급 취소
    onCancle();
    showCancle.onFalse();
    setSelectedCancle(null);
  };

  const showEdit = useBoolean();
  const [selectedEdit, setSelectedEdit] = useState(null);
  const handleEdit = (param, type) => {
    setSelectedEdit(param);
    setItemType(type);
    showEdit.onTrue();
  };

  const handleEditClose = () => {
    showEdit.onFalse();
    setSelectedEdit(null);
  };

  const handleEditSave = async (changeDate, type) => {
    try {
      const editData = {
        invUuid: selectedEdit.invUuid,
      };

      // 날짜 필드 설정
      if (type === 'reqComp') {
        editData.reqCompDt = dayjs(changeDate).format('YYYY-MM-DD');
      } else if (type === 'reqPlan') {
        editData.reqPlanDt = dayjs(changeDate).format('YYYY-MM-DD');
      } else if (type === 'rsltComp') {
        editData.rsltCompDt = dayjs(changeDate).format('YYYY-MM-DD');
      }

      const response = await taxApi.editTaxbill(editData);
      if (response.data.info) {
        handleEditClose();
        enqueueSnackbar('수정이 완료 되었습니다', {
          variant: 'success',
        });
        dispatch(listTaxbills(makeFilters(defaultFilters)));
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  // 세금계산서 리스트에서 발행 삭제 액션 제거(0425 p19-20/0515 p19-20 "삭제 기능은 제거").
  // 백엔드 taxApi.removeIssuedTaxbills 엔드포인트 자체는 유지.

  const onCancle = async () => {
    try {
      const editData = {
        invUuid: selectedCancle.invUuid,
        reqCompYn: 'N',
      };
      const response = await taxApi.editTaxbill(editData);
      if (response.data.info) {
        enqueueSnackbar('발급 취소 되었습니다', {
          variant: 'success',
        });
        dispatch(listTaxbills(makeFilters(defaultFilters)));
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

//handleRsltComplete
const showRsltComplete = useBoolean();
const [selectedRsltComplete, setSelectedRsltComplete] = useState(null);
const handleRsltComplete = (param) => {
  setSelectedRsltComplete(param);
  showRsltComplete.onTrue();
};

const handleRsltCompleteClose = () => {
  showRsltComplete.onFalse();
  setSelectedRsltComplete(null);
};

// const [selectedRsltItem, setSelectedRsltItem] = useState(null);
// const [removeRsltVisible, setRemoveRsltVisible] = useState(false);
// const [itemRsltType, setItemRsltType] = useState(null);

// const handleRsltRemove = useCallback((row) => {
//   setRemoveRsltVisible(true);
//   setSelectedRsltItem(row);
// }, []);


// const onRemoveRsltCancle = () => {
//   setRemoveRsltVisible(false);
//   setSelectedRsltItem(null);
// };
// const onRemoveRsltConfirm = () => {
//   //매출 취소
//   onRemove();
//   setRemoveRsltVisible(false);
//   setSelectedRsltItem(null);
// };



const handleRsltCompleteSave = async () => {
  try {
    const editData = {
      invUuid: selectedRsltComplete.invUuid,
      rsltCompYn: 'Y',
    };
    const response = await taxApi.editTaxbill(editData);
    if (response.data.info) {
      handleRsltCompleteClose();
      enqueueSnackbar('실적확정 완료 되었습니다', {
        variant: 'success',
      });
      dispatch(listTaxbills(makeFilters(defaultFilters)));
    }
  } catch (err) {
    let message =
      typeof err.response !== 'undefined'
        ? err.response.data.message
        : err.message;
    enqueueSnackbar(message, {
      variant: 'error',
    });
  }
};


const [selectedRsltCancle, setSelectedRsltCancle] = useState(null);
  const showRsltCancle = useBoolean();
  const handleRsltCancle = useCallback((row) => {
    showRsltCancle.onTrue();
    setSelectedRsltCancle(row);
  }, []);

  const onCancleRsltCancle = () => {
    showRsltCancle.onFalse();
    setSelectedRsltCancle(null);
  };
  const onCancleRsltConfirm = () => {
    //발급 취소
    onRsltCancle();
    showCancle.onFalse();
    setSelectedRsltCancle(null);
  };

  
  const onRsltCancle = async () => {
    try {
      const editData = {
        invUuid: selectedRsltCancle.invUuid,
        rsltCompYn: 'N',
      };
      const response = await taxApi.editTaxbill(editData);
      if (response.data.info) {
        enqueueSnackbar('발급 취소 되었습니다', {
          variant: 'success',
        });
        dispatch(listTaxbills(makeFilters(defaultFilters)));
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

 

//발급
  const showComplete = useBoolean();
  const [selectedComplete, setSelectedComplete] = useState(null);

  // 세금계산서 발행 요청 결재(스위치), 2026-10-21 - 스위치 ON이고 아직 발행요청 전이면
  // 단순 확인(AskDialog) 대신 결재선 선택 다이얼로그를 먼저 띄운다.
  const showApprovalRequest = useBoolean();

  const handleComplete = (param) => {
    setSelectedComplete(param);
    const isRequestStep = param?.reqPlanYn !== 'Y';
    if (isRequestStep && approvalEnabled) {
      showApprovalRequest.onTrue();
      return;
    }
    showComplete.onTrue();
  };

  const handleCompleteClose = () => {
    showComplete.onFalse();
    setSelectedComplete(null);
  };

  const handleApprovalRequestCancel = () => {
    showApprovalRequest.onFalse();
    setSelectedComplete(null);
  };

  const handleApprovalRequestConfirm = async (lineId) => {
    try {
      await taxApi.requestIssue(selectedComplete.invUuid, { lineId });
      showApprovalRequest.onFalse();
      setSelectedComplete(null);
      enqueueSnackbar('발행 요청 결재를 상신했습니다. 승인되면 발행완료 처리를 진행할 수 있습니다.', {
        variant: 'success',
      });
      dispatch(listTaxbills(makeFilters(defaultFilters)));
    } catch (err) {
      const message = err?.response?.data?.message || err.message;
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  // 2026-10-21: 발행 흐름 2단계 분리(이슈 #4). REQ_PLAN_YN !== 'Y' 이면 아직 발행요청 전이라
  // 먼저 발행요청만 하고, 발행요청이 이미 끝난 건(REQ_PLAN_YN === 'Y')만 발행완료로 넘어간다.
  // 결재 스위치가 ON이면 이 경로(AskDialog)는 발행요청 단계에서 호출되지 않는다(위
  // handleComplete가 결재선 선택 다이얼로그로 보낸다) - 여기는 발행완료 단계 + 스위치 OFF일
  // 때의 발행요청만 처리한다.
  const handleCompleteSave = async () => {
    const isRequestStep = selectedComplete?.reqPlanYn !== 'Y';
    try {
      if (isRequestStep) {
        await taxApi.requestIssue(selectedComplete.invUuid);
        handleCompleteClose();
        enqueueSnackbar('발행요청 되었습니다. 발행완료 버튼으로 마저 처리해 주세요.', {
          variant: 'success',
        });
        dispatch(listTaxbills(makeFilters(defaultFilters)));
        return;
      }

      const response = await taxApi.completeIssue(selectedComplete.invUuid);
      if (response.data.result === 'fail') {
        enqueueSnackbar(response.data.message || '결재 승인으로만 발행 완료됩니다.', {
          variant: 'warning',
        });
        return;
      }
      handleCompleteClose();
      enqueueSnackbar('발급완료 되었습니다', {
        variant: 'success',
      });
      dispatch(listTaxbills(makeFilters(defaultFilters)));
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const columns = useMemo(
    () => [
      { field: 'id', hide: true, sortable: false, editable: false },
      { field: 'invUuid', hide: true, sortable: false, editable: false }, //세금계산서ID
      { field: 'roUuid', hide: true, sortable: false, editable: false }, //수주ID
      { field: 'billUuid', hide: true, sortable: false, editable: false }, //거래ID
      { field: 'prjUuid', hide: true, sortable: false, editable: false }, //프로젝트ID
      {
        field: 'invYn',
        hide: true,
        editable: false,
      },
      {
        field: 'invNo',
        headerName: '계산서번호',
        hide: true,
        editable: false,
      },
      {
        field: 'invTyp',
        headerName: '유형',
        hide: true,
        editable: false,
      },

      {
        field: 'reqPlanDt',
        headerName: '발행예정일',
        description: '',
        align: 'center',
        headerAlign: 'center',
        type: 'date',
        flex: 0.8,
        minWidth: 150,
        valueGetter: (params) => {
          // 문자열 형식의 날짜를 Date 객체로 변환
          return params ? new Date(params) : null;
        },
        renderCell: (params) => {
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2">{params.row.reqPlanDt}</Typography>
              <IconButton
                aria-label="edit"
                color="primary"
                size="small"
                onClick={() => handleEdit(params.row, 'reqPlan')}
              >
                <EditIcon />
              </IconButton>
            </Stack>
          );
        },
      },
      {
        field: 'reqCompDt',
        headerName: '발행완료일',
        description: '',
        align: 'center',
        headerAlign: 'center',
        type: 'string',
        flex: 0.8,
        minWidth: 150,
        valueGetter: (params) => {
          // 문자열 형식의 날짜를 Date 객체로 변환
          return params ? new Date(params) : null;
        },
        renderCell: (params) => {
          
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2">{params.row.reqCompDt}</Typography>
              <IconButton
                aria-label="edit"
                color="primary"
                size="small"
                onClick={() => handleEdit(params.row, 'reqComp')}
              >
                <EditIcon />
              </IconButton>
            </Stack>
          );
        },
      },
      {
        field: 'action',
        headerName: '발행 처리',
        // Action 컬럼 축소(fit) - 0425 p19-20/0515 p19-20 시안
        width: 110,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const row = params.row;
          let renderAction;
          if (row.reqCompYn === 'Y') {
            if (row.procCompYn === 'Y') {
              renderAction = (
                <Typography
                  variant="subtitle2"
                  color="success.main"
                  sx={{ mx: 1 }}
                >
                  수금완료
                </Typography>
              );
            } else if (row.procCompYn === 'N') {
              renderAction = (
                <Button
                  size="small"
                  variant="contained"
                  color="inherit"
                  onClick={() => handleCancle(row)}
                >
                  발행취소
                </Button>
              );
            }
          } else if (row.reqCompYn === 'N') {
            // 세금계산서 발행 요청 결재(스위치), 2026-10-21 - 결재가 진행중인 회차
            // (TRJ_UUID가 'NO_KEY'가 아님)는 버튼 대신 상태만 보여준다. 승인/반려/회수는
            // 결재함(V2) 또는 수주품의서 관리 패널의 "결재 회수"에서 처리한다.
            if (row.trjUuid && row.trjUuid !== 'NO_KEY') {
              renderAction = <Chip size="small" color="warning" label="결재중" />;
            } else {
              // 2026-10-21 이슈 #4: 발행예정 → 발행요청 → 발행완료 2단계로 분리.
              // REQ_PLAN_YN !== 'Y' 이면 아직 발행요청 전이다.
              renderAction = (
                <Button
                  size="small"
                  variant="contained"
                  color={row.reqPlanYn === 'Y' ? 'success' : 'info'}
                  onClick={() => handleComplete(row)}
                >
                  {row.reqPlanYn === 'Y' ? '발행완료' : '발행요청'}
                </Button>
              );
            }
          }

          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              {renderAction}
            </Stack>
          );
        },
      },
      {
        field: 'rsltCompDt',
        headerName: '실적반영일',
        description: '',
        align: 'center',
        headerAlign: 'center',
        type: 'date',
        flex: 0.8,
        minWidth: 150,
        valueGetter: (params) => {
          // 문자열 형식의 날짜를 Date 객체로 변환
          return params ? new Date(params) : null;
        },
        renderCell: (params) => {
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2">{params.row.rsltCompDt}</Typography>
              <IconButton
                aria-label="edit"
                color="primary"
                size="small"
                onClick={() => handleEdit(params.row, 'rsltComp')}
              >
                <EditIcon />
              </IconButton>
            </Stack>
          );
        },
      },
      {
        field: 'action2',
        headerName: '실적 처리',
        // Action 컬럼 축소(fit) - 0425 p19-20/0515 p19-20 시안
        width: 120,
        editable: false,
        align: 'center',
        headerAlign: 'center',

        renderCell: (params) => {
          const { reqCompYn, rsltCompYn } = params.row;
      
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              {reqCompYn === 'Y' && rsltCompYn === 'N' && (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={() => handleRsltComplete(params.row)}
                >
                  실적확정
                </Button>
              )}
              {reqCompYn === 'Y' && rsltCompYn === 'Y' && (
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  onClick={() => handleRsltCancle(params.row)}
                >
                  확정취소
                </Button>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'prjNo',
        headerName: '프로젝트번호',
        minWidth: 150,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },
      {
        field: 'partnerNm',
        headerName: '고객명',
        minWidth: 150,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },

      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },
      {
        field: 'invTxt',
        headerName: '발급내용',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'invAmt',
        headerName: '계산서 금액',
        minWidth: 120,
        flex: 1,
        type: 'number',
      },
      {
        field: 'invVat',
        headerName: '부가세 금액',
        minWidth: 120,
        flex: 1,
        type: 'number',
      },
      {
        field: 'invTot',
        headerName: '합계 금액',
        minWidth: 150,
        flex: 1,
        type: 'number',
      },

      {
        field: 'procCompDt',
        headerName: '지급(수금)완료일자',
        description: '',
        align: 'center',
        headerAlign: 'center',
        flex: 0.8,
        minWidth: 180,
      },
      {
        field: 'procCompYn',
        headerName: '지급(수금) 완료 여부',
        description: '',
        align: 'center',
        headerAlign: 'center',
        flex: 0.8,
        minWidth: 120,
      },

      {
        field: 'reqPlanUsrNm',
        headerName: '발행요청자',
        description: '발행요청자',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },

      {
        field: 'salesUsrNm',
        headerName: '영업사원',
        description: '영업사원',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },
    ],
    // approvalEnabled: action 컬럼의 renderCell이 handleComplete 분기(결재 스위치 ON이면
    // 결재선 선택 다이얼로그)를 캡처한다 - 의존성 배열이 비어 있으면 최초 렌더(fetch 전,
    // 항상 false) 값에 고정되어 스위치를 켜도 영원히 구버전 AskDialog로 빠진다(재현해서 발견,
    // 2026-10-21).
    [approvalEnabled],
  );

  //검색 관련 사항
  const initialValues = {
    searchKeyword: defaultFilters.searchKeyword,
    searchWords: defaultFilters.searchWords,
    searchStrtDt: defaultFilters.searchStrtDt,
    searchEndDt: defaultFilters.searchEndDt,
  };

  const validationSchema = yup.object({
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '검색 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('검색 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      const searchData = {
        searchOsTran: defaultFilters.searchOsTran,
        searchInvTyp: defaultFilters.searchInvTyp,
        searchTyp: defaultFilters.searchTyp,
        reqCompYn: defaultFilters.searchYn,
        searchKeyword: values.searchKeyword,
        searchWords: values.searchWords,
        searchStrtDt: values.searchStrtDt,
        searchEndDt: values.searchEndDt,
      };
      dispatch(setTaxbillsFilters(searchData));
      setTimeout(() => {
        setSubmitting(false);
      }, 1000);
    },
  });

  return (
    <>
      <BreadcrumbsContainer
        title="계산서 채권채무/세금계산서 관리/세금계산서 리스트"
        icon="pay"
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        <TaxbillListBox formik={formik} onReset={() => dispatch(resetTaxbillsFilters())} />
        <TaxbillStatusList filters={defaultFilters} onFilters={handleFilters} />
        <TaxbillTotalSummary totalPrice={totalPrice} />
        <TaxbillList
          columns={columns}
          rows={tableData}
          error={error}
          loading={loading}
          columnVisibilityModel={{
            id: false,
            invNo: false,
            invUuid: false,
            roUuid: false,
            billUuid: false,
            prjUuid: false,
            invYn: false,
            invTyp: false,
          }}

        />
      </Box>
      <AskDialog
        visible={showCancle.value}
        title="발급 취소"
        description={`해당 ${selectedCancle?.invTyp || '매출'} "${selectedCancle?.invTxt}" 을 발급 취소하시겠습니까?`}
        onConfirm={onCancleConfirm}
        onCancle={onCancleCancle}
      />
      <AskDialog
        visible={showComplete.value}
        title={selectedComplete?.reqPlanYn === 'Y' ? '발급 완료' : '발행 요청'}
        description={
          selectedComplete?.reqPlanYn === 'Y'
            ? `해당 ${selectedComplete?.invTyp || '매출'} "${selectedComplete?.invTxt}" 을 발급 완료하시겠습니까?`
            : `해당 ${selectedComplete?.invTyp || '매출'} "${selectedComplete?.invTxt}" 을 발행요청 하시겠습니까? (발행요청 후 발행완료 버튼으로 마저 처리합니다)`
        }
        onConfirm={handleCompleteSave}
        onCancle={handleCompleteClose}
      />
      <IssueApprovalRequestDialog
        open={showApprovalRequest.value}
        target={selectedComplete}
        onCancel={handleApprovalRequestCancel}
        onConfirm={handleApprovalRequestConfirm}
      />
      <AskDialog
        visible={showRsltComplete.value}
        title="실적 확정 완료"
        description={`해당 ${selectedRsltComplete?.invTyp || '매출'} "${selectedRsltComplete?.invTxt}" 을 실적 확정하시겠습니까?`}
        onConfirm={handleRsltCompleteSave}
        onCancle={handleRsltCompleteClose}
      />
      <AskDialog
        visible={showRsltCancle.value}
        title="실적 확정 취소"
        description={`해당 ${selectedRsltComplete?.invTyp || '매출'} "${selectedRsltComplete?.invTxt}" 을 실적 확정을 취소하시겠습니까?`}
        onConfirm={onCancleRsltConfirm}
        onCancle={onCancleRsltCancle}
      />
      {showEdit && (
        <TaxbillEditModal
          currentData={selectedEdit}
          open={showEdit.value}
          handleClose={handleEditClose}
          onSave={(changeDate, type) => handleEditSave(changeDate, type)}
          editType={itemType} 
        />
      )}
    </>
  );
};

export default TaxbillListContainer;
